import React, { useEffect } from 'react'
import cn from 'classnames'
import styles from 'components/shared/tray.module.scss'
import { X } from 'lucide-react'

type Props = {
    title?: React.ReactNode
    onDismiss: () => void
    children: React.ReactNode
    className?: string
    trayClassName?: string
    bodyClassName?: string
    showClose?: boolean
    closeLabel?: string
    preventEscClose?: boolean
    preventBackdropClose?: boolean
    dialogTestId?: string
    closeButtonTestId?: string
}

export default function Tray({
    title,
    onDismiss,
    children,
    className,
    trayClassName,
    bodyClassName,
    showClose = true,
    closeLabel = 'Close',
    preventEscClose = false,
    preventBackdropClose = false,
    dialogTestId,
    closeButtonTestId,
}: Props) {
    useEffect(() => {
        if (preventEscClose) return

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onDismiss()
            }
        }

        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [onDismiss, preventEscClose])

    return (
        <div
            className={cn(styles.overlay, className)}
            onClick={preventBackdropClose ? undefined : onDismiss}
        >
            <div
                className={cn(styles.tray, trayClassName)}
                onClick={e => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                data-testid={dialogTestId}
            >
                {(title != null || showClose) && (
                    <div className={styles.header}>
                        <div className={styles.headerSlot} />
                        <div className={styles.title}>{title}</div>
                        <div className={styles.headerSlot}>
                            {showClose && (
                                <button
                                    className={styles.iconButton}
                                    onClick={onDismiss}
                                    aria-label={closeLabel}
                                    data-testid={closeButtonTestId}
                                >
                                    <X size={18} />
                                </button>
                            )}
                        </div>
                    </div>
                )}

                <div className={cn(styles.body, bodyClassName)}>{children}</div>
            </div>
        </div>
    )
}
